// @ts-check
import { Component } from '@theme/component';
import { debounce, fetchConfig } from '@theme/utilities';
import { CartUpdateEvent } from '@theme/events';
import { visibleCartCount } from '@theme/custom.cart-visible-count';
import {
  ADDON_ID_PROPERTY,
  ADDON_PARENT_PROPERTY,
  ensureAddonId,
  findAddonParent,
  queueCartAddonMutation,
  readAddonCart,
} from '@theme/custom.cart-addon-links';

/**
 * @typedef {object} CartUpsellsRefs
 * @property {HTMLTextAreaElement} [giftMessage] - The gift-message textarea.
 * @property {HTMLElement} [giftCounter] - The "n/240" character counter.
 * @property {HTMLElement} [giftMessageError] - The "message not saved" line under the textarea.
 * @property {HTMLElement} [protectionOptions] - The protection-plan radio group.
 * @property {HTMLElement} [upsellError] - The tiles' shared error line, revealed when a mutation fails.
 */

/**
 * The subset of a /cart/change.js response this component reads. `sections` rides along only when
 * the call asked for a section bundle.
 * @typedef {object} CartPayload
 * @property {import('./custom.cart-addon-links').AddonCartLine[]} [items]
 * @property {Record<string, string>} [sections]
 */

/** @typedef {{ variantId: number, included: boolean }} ProtectionSelection */

const PLAN_SELECTION_DEBOUNCE_MS = 150;

/** Marks a checkout button this module disabled, so release never re-enables one Liquid disabled. */
const CHECKOUT_LOCK_ATTRIBUTE = 'data-upsell-locked';

/**
 * A tier switch adds the new plan before it removes the old one, so for about a second the cart
 * bills both. Every checkout button waits until no tile is busy so no checkout carries both plans.
 */
function syncCheckoutLock() {
  const busy = document.querySelector('custom-cart-upsells [aria-busy="true"]') !== null;
  for (const button of document.querySelectorAll('button[name="checkout"]')) {
    if (!(button instanceof HTMLButtonElement)) continue;
    if (busy) {
      button.disabled = true;
      button.setAttribute(CHECKOUT_LOCK_ATTRIBUTE, '');
    } else if (button.hasAttribute(CHECKOUT_LOCK_ATTRIBUTE)) {
      button.disabled = false;
      button.removeAttribute(CHECKOUT_LOCK_ATTRIBUTE);
    }
  }
}

/**
 * Mutates one cart line's gift-bag and protection-plan tiles; the cart remains the source of truth.
 * Section HTML refreshes every result, including failures, so stale optimistic selections cannot persist.
 *
 * @extends {Component<CartUpsellsRefs>}
 */
class CustomCartUpsells extends Component {
  /**
   * Serializes cart mutations because concurrent requests can race server-side.
   * Carries each result forward; only draft saving reads it.
   * @type {Promise<unknown>}
   */
  #queue = Promise.resolve();

  /** @type {ProtectionSelection | undefined} */
  #desiredProtectionSelection;

  #protectionRevision = 0;
  #appliedProtectionRevision = 0;

  /** Whether a gift-bag mutation is still in flight. See #applyGiftBusy for why it lives here. */
  #giftBusy = false;

  /** The shopper opened the protection options this session; no morph may close them. */
  #protectionTileOpen = false;

  #protectionStateObserver = new MutationObserver(() => {
    this.#restorePendingProtectionSelection();
    this.#restoreProtectionTile();
    this.#applyProtectionBusy();
    this.#applyGiftBusy();
  });

  #queueProtectionSelection = debounce(() => {
    void this.#mutate(() => this.#flushProtectionSelection()).finally(() =>
      this.#applyProtectionBusy()
    );
  }, PLAN_SELECTION_DEBOUNCE_MS);

  connectedCallback() {
    super.connectedCallback();
    this.#protectionStateObserver.observe(this, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['checked'],
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#protectionStateObserver.disconnect();
    this.#queueProtectionSelection.cancel();
  }

  /**
   * Collapses rapid protection-tier changes to the latest choice, even during a request.
   * Intermediate mutations stay silent so stale section HTML cannot replay through the drawer.
   * @param {Event} event
   */
  selectPlan(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    const variantId = Number(input.value);
    if (!Number.isInteger(variantId) || variantId <= 0) return;

    this.#protectionTileOpen = true;
    this.#requestProtectionSelection(variantId, input.hasAttribute('data-included'));
  }

  /**
   * @param {number} variantId
   * @param {boolean} included
   */
  #requestProtectionSelection(variantId, included) {
    this.#desiredProtectionSelection = { variantId, included };
    this.#protectionRevision++;
    this.#applyProtectionBusy();
    this.#queueProtectionSelection();
  }

  /**
   * Converges on the latest requested tier before publishing section HTML once.
   * A newer click advances the revision and replaces the in-flight target.
   */
  async #flushProtectionSelection() {
    while (this.#appliedProtectionRevision !== this.#protectionRevision) {
      const selection = this.#desiredProtectionSelection;
      if (!selection) return;

      const revision = this.#protectionRevision;
      try {
        const { changed, sections } = await this.#applyProtectionSelection(selection);
        if (revision !== this.#protectionRevision) continue;

        // A tier the cart already held renders nothing new, and a section fetch plus a
        // drawer morph for an unchanged cart is what moves the shopper's scroll position.
        if (changed) {
          const html = sections ?? (await this.#fetchSections());
          if (revision !== this.#protectionRevision) continue;
          this.#dispatchUpdate(html);
        }
      } catch (error) {
        // Settle the failed revision too: #mutate re-syncs the tiles from the cart, and a
        // revision left pending repaints the refused tier over that render, never billed.
        this.#appliedProtectionRevision = revision;
        throw error;
      }

      this.#appliedProtectionRevision = revision;
    }
  }

  /**
   * Applies one tier without morphing the UI. Paid-to-paid switches add first
   * so a rejected add retains the current protection line.
   *
   * Every call here is a sequential Shopify round trip the shopper waits for, so the switch
   * is kept to one read, the add and one remove; the remove carries the section HTML the
   * caller morphs, saving a fourth request.
   * @param {ProtectionSelection} selection
   * @returns {Promise<{ changed: boolean, sections?: Record<string, string> }>} Whether the
   *   cart changed, with the section bundle of the last mutation when it made one.
   */
  async #applyProtectionSelection(selection) {
    // Read the cart, not the dataset: concurrent morphs can overwrite it with stale section HTML.
    // Acting on stale data can remove the wrong line or skip a tier the cart still holds.
    const cart = await readAddonCart();
    const existing = await this.#currentProtectionLine(undefined, undefined, cart);

    if (selection.included) {
      if (!existing) return { changed: false };
      const removed = await this.#change({ id: existing.key, quantity: 0 }, { sections: true });
      return { changed: true, sections: removed.sections };
    }

    if (existing?.variant_id === selection.variantId) return { changed: false };

    const added = await this.#add(selection.variantId, {}, { silent: true, cart });
    const addedLine = added.items?.[0];
    const addedKey = addedLine?.key;
    if (!addedKey) throw new Error('Protection plan add returned no line key');
    if (!existing) return { changed: true };

    try {
      // A cart discount can re-key lines on the add, so on failure re-read the plan and retry once
      // under its new key. An unchanged key means Shopify refused the removal; that error stands.
      let removed;
      try {
        removed = await this.#change({ id: existing.key, quantity: 0 }, { sections: true });
      } catch (changeError) {
        const parentKey = addedLine.parent_relationship?.parent_key ?? this.dataset.itemKey;
        const previous = await this.#currentProtectionLine(parentKey, selection.variantId);
        if (!previous) throw new Error('Protection plan to replace was not found');
        if (previous.key === existing.key) throw changeError;
        removed = await this.#change({ id: previous.key, quantity: 0 }, { sections: true });
      }
      return { changed: true, sections: removed.sections };
    } catch (removeError) {
      // Keep the old plan if a switch cannot finish. Otherwise a transient
      // removal failure leaves both paid lines in the cart.
      try {
        await this.#removeAddon(
          { key: addedKey, variant_id: selection.variantId },
          { silent: true }
        );
      } catch (rollbackError) {
        console.error('Protection plan rollback failed', rollbackError);
      }
      throw removeError;
    }
  }

  /**
   * Reads this row's live protection line from the cart, not the last-rendered section HTML.
   * Its radio values identify plan variants nested under this parent.
   * @param {string} [parentKey] - The row's key as the cart holds it now, when a mutation re-keyed it.
   * @param {number} [exceptVariantId] - A tier to skip, so a switch can find the plan it replaces.
   * @param {CartPayload} [cart] - A cart read moments ago, when the caller reuses it for the add.
   * @returns {Promise<{ key: string, variant_id: number } | undefined>}
   */
  async #currentProtectionLine(parentKey = this.dataset.itemKey, exceptVariantId, cart) {
    cart ??= await readAddonCart();
    const inputs = /** @type {NodeListOf<HTMLInputElement>} */ (
      this.querySelectorAll('.custom-cart-protect__input')
    );
    const variantIds = new Set([...inputs].map((input) => Number(input.value)));
    return cart.items?.find(
      (line) =>
        this.#belongsToParent(line, parentKey) &&
        variantIds.has(line.variant_id) &&
        line.variant_id !== exceptVariantId
    );
  }

  /**
   * @param {import('./custom.cart-addon-links').AddonCartLine} line
   * @param {string} [parentKey]
   */
  #belongsToParent(line, parentKey = this.dataset.itemKey) {
    return this.dataset.addonParent
      ? line.properties?.[ADDON_PARENT_PROPERTY] === this.dataset.addonParent
      : line.parent_relationship?.parent_key === parentKey;
  }

  /**
   * @param {{ key: string, variant_id: number }} line
   * @param {{ silent?: boolean }} [options]
   */
  async #removeAddon(line, options = {}) {
    if (this.dataset.addonParent) {
      const cart = await readAddonCart();
      const fresh = cart.items.find(
        (item) => this.#belongsToParent(item) && item.variant_id === line.variant_id
      );
      if (!fresh) return;
      line = fresh;
    }
    return this.#change({ id: line.key, quantity: 0 }, options);
  }

  async #currentGiftLine() {
    const cart = await readAddonCart();
    return cart.items.find(
      (line) =>
        this.#belongsToParent(line) && line.variant_id === Number(this.dataset.giftVariantId)
    );
  }

  /**
   * Re-derives busy from the revisions after each morph, rather than leaving it on a replaced
   * node: a morph mid-mutation would otherwise announce a group that is still in flight as idle.
   */
  #applyProtectionBusy() {
    const options = this.refs.protectionOptions;
    if (!options) return;

    if (this.#appliedProtectionRevision !== this.#protectionRevision)
      options.setAttribute('aria-busy', 'true');
    else options.removeAttribute('aria-busy');
    syncCheckoutLock();
  }

  /** Keeps an unrelated cart morph from briefly repainting the server's stale tier. */
  #restorePendingProtectionSelection() {
    if (this.#appliedProtectionRevision === this.#protectionRevision) return;

    const variantId = this.#desiredProtectionSelection?.variantId;
    if (!variantId) return;

    const input = this.querySelector(`.custom-cart-protect__input[value="${variantId}"]`);
    if (input instanceof HTMLInputElement) input.checked = true;
  }

  /**
   * Unchecking removes a paid plan; checking adds nothing because the included tier is the default.
   * @param {Event} event
   */
  toggleProtection(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    this.#protectionTileOpen = input.checked;
    if (input.checked) return;

    const included = this.querySelector('.custom-cart-protect__input[data-included]');
    if (!(included instanceof HTMLInputElement)) return;

    included.checked = true;
    const variantId = Number(included.value);
    if (Number.isInteger(variantId) && variantId > 0) {
      this.#requestProtectionSelection(variantId, true);
    }
  }

  /**
   * The section HTML checks the protection tile only while a paid plan is in the cart, and the
   * included tier adds no line. Without this, the morph after picking "2 years" unchecks and
   * collapses the options the shopper is looking at, as if they had declined protection.
   */
  #restoreProtectionTile() {
    if (!this.#protectionTileOpen) return;
    const toggle = this.refs.protectionOptions
      ?.closest('.custom-cart-option-tile')
      ?.querySelector('.custom-cart-option-tile__toggle');
    if (toggle instanceof HTMLInputElement && !toggle.checked) toggle.checked = true;
  }

  /**
   * Unchecking the gift tile removes the bag line, keeping the checkbox and
   * the cart in agreement - otherwise the next re-render would re-check it.
   * @param {Event} event
   */
  toggleGiftTile(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.checked) return;

    if (this.dataset.addonParent) {
      void this.#mutate(async () => {
        const line = await this.#currentGiftLine();
        if (line) await this.#removeAddon(line);
      });
    } else {
      const key = this.dataset.giftLineKey;
      if (key) void this.#mutate(() => this.#change({ id: key, quantity: 0 }));
    }
  }

  /** The gift CTA: adds the bag (with any typed message) or removes it. */
  toggleGiftBag() {
    if (this.dataset.addonParent) {
      const message = this.refs.giftMessage?.value.trim() ?? '';
      void this.#withGiftBusy(() =>
        this.#mutate(async () => {
          const existing = await this.#currentGiftLine();
          if (existing) return this.#removeAddon(existing);
          const variantId = Number(this.dataset.giftVariantId);
          if (variantId) return this.#add(variantId, message ? { 'Gift message': message } : {});
          return undefined;
        })
      );
      return;
    }
    const key = this.dataset.giftLineKey;

    if (key) {
      void this.#withGiftBusy(() => this.#mutate(() => this.#change({ id: key, quantity: 0 })));
      return;
    }

    const variantId = Number(this.dataset.giftVariantId);
    if (!variantId) return;

    const message = this.refs.giftMessage?.value.trim() ?? '';
    void this.#withGiftBusy(() =>
      this.#mutate(() => this.#add(variantId, message ? { 'Gift message': message } : {}))
    );
  }

  /**
   * Holds the gift control inert through the request, section morph, and any queued plan switch.
   *
   * @param {() => Promise<unknown>} action
   */
  async #withGiftBusy(action) {
    // Disabling blurs the button and the morph swaps it for its twin; focus would land on <body>.
    const hadFocus = document.activeElement?.matches(
      '.custom-cart-gift__cta, .custom-cart-gift__remove'
    );
    this.#giftBusy = true;
    this.#applyGiftBusy();

    try {
      await action();
    } finally {
      this.#giftBusy = false;
      this.#applyGiftBusy();
      // Only when focus is still lost: a shopper who moved on during the request keeps their place.
      if (hadFocus && document.activeElement === document.body) {
        const control = this.querySelector('.custom-cart-gift__cta, .custom-cart-gift__remove');
        if (control instanceof HTMLElement) control.focus({ preventScroll: true });
      }
    }
  }

  /**
   * Applies busy state to the current gift control after each morph, rather than to a replaced
   * button. Use real `disabled`: delegated Enter clicks bypass an aria-only disabled state.
   */
  #applyGiftBusy() {
    // Exactly one of the two is rendered - the CTA before the bag is on the line, the trash after.
    const control = this.querySelector('.custom-cart-gift__cta, .custom-cart-gift__remove');
    if (!(control instanceof HTMLButtonElement) || control.disabled === this.#giftBusy) return;

    control.disabled = this.#giftBusy;
    if (this.#giftBusy) control.setAttribute('aria-busy', 'true');
    else control.removeAttribute('aria-busy');
    syncCheckoutLock();
  }

  /** Silent draft save (stock cart-note pattern): no section bundle/event, since re-rendering
   * while typing would clobber the textarea; payload skips quantity so edits stay untouched. */
  #saveMessageDraft = debounce(() => {
    const key = this.dataset.giftLineKey;
    const message = this.refs.giftMessage?.value.trim() ?? '';
    const previous = this.dataset.giftMessage ?? '';
    if (!key || message === previous) return;

    // Keep the compare-guard current without waiting for a re-render.
    this.dataset.giftMessage = message;

    // Do not route through #mutate: its failure re-sync would clobber the typed draft.
    // Keep the textarea, restore the compare guard for retry, and show the error inline.
    this.#queue = this.#queue
      .then(() =>
        queueCartAddonMutation(async () => {
          const line = await this.#currentGiftLine();
          if (!line) throw new Error('Gift bag is no longer in the cart');
          return this.#change(
            { id: line.key, properties: { ...line.properties, 'Gift message': message } },
            { silent: true }
          );
        })
      )
      .then((cart) => {
        // Properties change a line key; silent saves do not let Liquid refresh it. Read the
        // returned cart so later saves, removal, and checkbox state target the renamed line.
        const fresh = this.#giftLineKey(cart);
        // Do not guess when the returned cart cannot identify the line; a stale key fails loudly.
        // A guessed key could silently edit another line.
        if (fresh) this.dataset.giftLineKey = fresh;
        if (this.refs.giftMessageError) this.refs.giftMessageError.hidden = true;
        // Saved and not typed over since: a later morph may bring another tab's message.
        const field = this.refs.giftMessage;
        if (field?.value.trim() === message) field.toggleAttribute('data-preserve-draft', false);
      })
      .catch((error) => {
        console.error(error);
        this.dataset.giftMessage = previous;
        if (this.refs.giftMessageError) this.refs.giftMessageError.hidden = false;
      });
  }, 300);

  /**
   * Updates the live counter and debounces draft saving.
   * Without a bag line, the message waits for the CTA to add it.
   * @param {Event} event
   */
  onMessageInput(event) {
    if (!(event.target instanceof HTMLTextAreaElement)) return;

    // Unsaved text only: morph.js writes the server value back over a textarea without the flag.
    const unsaved = event.target.value.trim() !== (this.dataset.giftMessage ?? '');
    event.target.toggleAttribute('data-preserve-draft', unsaved);
    this.#renderGiftCount();

    this.#saveMessageDraft();
  }

  updatedCallback() {
    super.updatedCallback();
    // The morph brings the server's count; a kept draft is longer than it says.
    this.#renderGiftCount();
  }

  #renderGiftCount() {
    const { giftCounter, giftMessage } = this.refs;
    if (giftCounter && giftMessage) giftCounter.textContent = `${giftMessage.value.length}/240`;
  }

  /**
   * Enqueues one mutation; any failure re-syncs the UI from the cart -
   * snapping the tiles back to its true state - and reveals the error line.
   * @param {() => Promise<unknown>} action
   * @returns {Promise<unknown>}
   */
  #mutate(action) {
    this.#queue = this.#queue
      .then(() => queueCartAddonMutation(action))
      .catch(async (error) => {
        console.error(error);

        // Fetch sections before dispatch: synchronous morphs must finish before the error reveal.
        /** @type {Record<string, string> | undefined} */
        let sections;
        try {
          sections = await this.#fetchSections();
        } catch {
          // Sections-less event still re-syncs, just asynchronously - that
          // re-render then hides the error line again. Degraded, not broken.
        }
        this.dispatchEvent(
          new CartUpdateEvent({}, this.id, { source: 'custom-cart-upsells', sections })
        );

        const { upsellError } = this.refs;
        if (upsellError) upsellError.hidden = false;
      });
    return this.#queue;
  }

  /**
   * Adds a native child or a property-linked bundle add-on through `/cart/add.js`.
   * Quantity stays one regardless of the parent quantity.
   *
   * @param {number} variantId
   * @param {Record<string, string>} [properties] - Line properties, e.g. the gift message.
   * @param {{ silent?: boolean, cart?: CartPayload }} [options] - silent skips the section bundle
   *   and the event; used when a change immediately follows. cart is a read the caller just made,
   *   saving a second round trip on every tier switch.
   * @returns {Promise<CartPayload>} The added lines and optional section bundle.
   */
  async #add(variantId, properties = {}, options = {}) {
    /** @type {{ id: number, quantity: number, properties: Record<string, string>, parent_line_key?: string }} */
    let item;
    if (this.dataset.bundle || this.dataset.addonParent) {
      const cart = options.cart ?? (await readAddonCart());
      const parent = findAddonParent(cart.items ?? [], this.dataset);
      if (!parent) throw new Error('Bundle is no longer in the cart');
      // A bundle's first add-on mints the id it links by. The tile learns it here rather than from
      // the morph, which lands only after the whole mutation and may still be a tier switch away.
      const owner = await ensureAddonId(parent);
      this.dataset.addonParent = owner.properties?.[ADDON_ID_PROPERTY];
      item = {
        id: variantId,
        quantity: 1,
        properties: { ...properties, [ADDON_PARENT_PROPERTY]: this.dataset.addonParent ?? '' },
      };
    } else {
      item = { id: variantId, quantity: 1, parent_line_key: this.dataset.itemKey, properties };
    }
    const body = JSON.stringify(
      options.silent
        ? { items: [item] }
        : {
            items: [item],
            sections: this.#sectionIds().join(','),
            sections_url: window.location.pathname,
          }
    );

    const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
    const parsed = await response.json();

    // `description` is shopper-facing and `message` generic, but any non-2xx response still fails.
    // Do not trust the response shape alone.
    if (!response.ok || parsed.status)
      throw new Error(parsed.description ?? parsed.message ?? 'Cart add failed');

    if (!options.silent) this.#dispatchUpdate(parsed.sections);
    return parsed;
  }

  /**
   * Returns this parent line's nested gift-bag key, mirroring the Liquid rule.
   * The parent link distinguishes identical gift-bag variants under different parent lines.
   * @param {CartPayload} cart
   * @returns {string | undefined}
   */
  #giftLineKey(cart) {
    const variantId = Number(this.dataset.giftVariantId);
    return cart.items?.find((line) => line.variant_id === variantId && this.#belongsToParent(line))
      ?.key;
  }

  /**
   * POST /cart/change.js.
   * @param {{ id: string, quantity?: number, properties?: Record<string, string> }} change
   * @param {{ silent?: boolean, sections?: boolean }} [options] - silent skips the section
   *   bundle and the event; used when an add immediately follows. sections asks for the bundle
   *   but leaves the event to the caller, for the last call of a multi-step mutation.
   * @returns {Promise<CartPayload>} The cart as it stands after the change.
   */
  async #change(change, options = {}) {
    const payload =
      options.silent && !options.sections
        ? change
        : {
            ...change,
            sections: this.#sectionIds().join(','),
            sections_url: window.location.pathname,
          };

    const response = await fetch(
      Theme.routes.cart_change_url,
      fetchConfig('json', { body: JSON.stringify(payload) })
    );
    const parsed = await response.json();

    // change.js reports errors as { errors } (string or object) - but some
    // failures come back as { status, message } instead, so any non-2xx throws.
    if (!response.ok || parsed.errors) {
      throw new Error(
        typeof parsed.errors === 'string' ? parsed.errors : (parsed.message ?? 'Cart change failed')
      );
    }

    if (!options.silent && !options.sections) this.#dispatchUpdate(parsed.sections);
    return parsed;
  }

  /**
   * Section ids of every cart surface on the page (cart page and drawer).
   * @returns {string[]}
   */
  #sectionIds() {
    const ids = new Set();
    for (const element of document.querySelectorAll('cart-items-component')) {
      if (element instanceof HTMLElement && element.dataset.sectionId)
        ids.add(element.dataset.sectionId);
    }
    return [...ids];
  }

  /** Fetches fresh HTML for every cart surface without changing the cart. */
  async #fetchSections() {
    const url = new URL(window.location.href);
    url.searchParams.set('sections', this.#sectionIds().join(','));

    const response = await fetch(url);
    if (!response.ok) throw new Error('Cart sections refresh failed');

    /** @type {Record<string, string>} */
    const sections = await response.json();
    return sections;
  }

  /**
   * Sends fresh section HTML to the event bus for CartItemsComponent to morph.
   * The cart icon and live regions derive the same visible count from those sections.
   * @param {Record<string, string> | undefined} sections
   */
  #dispatchUpdate(sections) {
    this.dispatchEvent(
      new CartUpdateEvent({}, this.id, {
        source: 'custom-cart-upsells',
        itemCount: visibleCartCount(sections),
        sections,
      })
    );
  }
}

if (!customElements.get('custom-cart-upsells')) {
  customElements.define('custom-cart-upsells', CustomCartUpsells);
}
